import { sanitizeInput, isValidEmail, sanitizeOrgId, isValidUUID } from '../../utils/security';
import { createApiClient } from './apiClient';
import { convertPhoneToBackendFormat } from '../../utils/phoneUtils';

interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  role: 'school_champion' | 'district_champion' | 'tm_admin';
  orgId: string;
  phoneNumber: string;
  isDesignated?: string;
  parentOrgId?: string;
}

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  password?: string;
  preferences?: {
    lang?: string;
  };
  orgId?: string;
  isDesignated?: string;
}



// Validate phone number format for user creation
// Added to prevent "Invalid phone number format" API errors
const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return true; // Empty phone numbers are allowed
  }
  
  // Remove all non-digits to check length
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Must be 10 digits (US) or 11 digits starting with 1 (US with country code)
  return (digits.length === 10) || (digits.length === 11 && digits.startsWith('1'));
};

// Validate user data before sending to API
const validateUserData = (userData: CreateUserData | UpdateUserData): void => {
  if ('email' in userData && userData.email && !isValidEmail(userData.email)) {
    throw new Error('Invalid email format');
  }
  
  // Validate phone number format before API call to provide better error messages
  if ('phoneNumber' in userData && userData.phoneNumber && !isValidPhoneNumber(userData.phoneNumber)) {
    throw new Error('Invalid phone number format. Please use a 10-digit US phone number.');
  }
  
  if ('firstName' in userData && userData.firstName) {
    if (userData.firstName.length > 50) {
      throw new Error('First name too long');
    }
    userData.firstName = sanitizeInput(userData.firstName);
  }
  
  if ('lastName' in userData && userData.lastName) {
    if (userData.lastName.length > 50) {
      throw new Error('Last name too long');
    }
    userData.lastName = sanitizeInput(userData.lastName);
  }

  // Validate password if provided
  if ('password' in userData && userData.password) {
    if (userData.password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
  }
};

// User API Service
export const userService = {
  // Get users for an organization
  getUsersForOrg: async (orgId: string) => {
    const sanitizedOrgId = sanitizeOrgId(orgId);
    const apiClient = createApiClient();
    const response = await apiClient.get(`/users/${sanitizedOrgId}`);
    const users = response.data.users || response.data;
    
    // Transform API response to match frontend interface
    return users.map((user: Record<string, unknown>) => ({
      ...user,
      userId: user.username, // Map username to userId for frontend compatibility
    }));
  },

  // Get a specific user by ID
  getUser: async (userId: string) => {
    if (!isValidUUID(userId)) {
      throw new Error('Invalid user ID format');
    }
    const apiClient = createApiClient();
    const response = await apiClient.get(`/user/${userId}`);
    return response.data;
  },

  // Update a user
  updateUser: async (userId: string, userData: UpdateUserData) => {
    if (!isValidUUID(userId)) {
      throw new Error('Invalid user ID format');
    }
    validateUserData(userData);
    const apiClient = createApiClient();
    const response = await apiClient.post(`/user/${userId}`, userData);
    return response.data;
  },

  // Update user profile (for profile settings)
  updateUserProfile: async (userId: string, userData: UpdateUserData) => {
    if (!isValidUUID(userId)) {
      throw new Error('Invalid user ID format');
    }
    validateUserData(userData);
    const apiClient = createApiClient();
    
    // Format phone number for backend if present
    const payload = { ...userData };
    if (payload.phoneNumber) {
      payload.phoneNumber = convertPhoneToBackendFormat(payload.phoneNumber) || '';
    }
    
    const response = await apiClient.post(`/user/${userId}`, payload);
    return response.data;
  },

  // Create a new user
  createUser: async (userData: CreateUserData) => {
    validateUserData(userData);
    const apiClient = createApiClient();
    
    // For admin users, only send the required fields
    if (userData.role === 'tm_admin') {
      const adminPayload = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        role: userData.role,
      };
      const response = await apiClient.post('/user', adminPayload);
      return response.data;
    }
    
    // For other user types, send the full payload including parentOrgId if present
    const payload = { ...userData };
    if (userData.parentOrgId) {
      payload.parentOrgId = userData.parentOrgId;
    }
    
    // Format phone number for backend if present (ensures proper +1 prefix)
    if (payload.phoneNumber) {
      payload.phoneNumber = convertPhoneToBackendFormat(payload.phoneNumber) || '';
    }
    
    const response = await apiClient.post('/user', payload);
    return response.data;
  },

  // Create a school champion user
  createSchoolChampion: async (userData: CreateUserData) => {
    validateUserData(userData);
    const apiClient = createApiClient();
    
    const payload = {
      ...userData,
      role: 'school_champion',
      isDesignated: userData.isDesignated || '1',
      // Include parentOrgId for Cognito custom attributes
      parentOrgId: userData.parentOrgId,
    };
    
    // Format phone number for backend if present (ensures proper +1 prefix)
    if (payload.phoneNumber) {
      payload.phoneNumber = convertPhoneToBackendFormat(payload.phoneNumber) || '';
    }
    
    const response = await apiClient.post('/user', payload);
    return response.data;
  },

  // Create a district champion user
  createDistrictChampion: async (userData: CreateUserData) => {
    validateUserData(userData);
    const apiClient = createApiClient();
    
    const payload = {
      ...userData,
      role: 'district_champion',
    };
    
    // Format phone number for backend if present (ensures proper +1 prefix)
    if (payload.phoneNumber) {
      payload.phoneNumber = convertPhoneToBackendFormat(payload.phoneNumber) || '';
    }
    
    const response = await apiClient.post('/user', payload);
    return response.data;
  },

  // Update organization champion
  updateOrganizationChampion: async (userId: string, userData: UpdateUserData) => {
    if (!isValidUUID(userId)) {
      throw new Error('Invalid user ID format');
    }
    validateUserData(userData);
    const apiClient = createApiClient();
    const response = await apiClient.post(`/user/${userId}`, {
      ...userData,
      isDesignated: userData.isDesignated || '1',
    });
    return response.data;
  },

  // Get all users
  getAllUsers: async () => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/users');
    return response.data.users || response.data;
  },

  // Get all admin users
  getAdminUsers: async () => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/users/');
    return response.data.users || response.data;
  },

  // Delete a user (Cognito username may be a UUID or an email alias)
  deleteUser: async (userId: string) => {
    if (!userId?.trim()) {
      throw new Error('Invalid user ID format');
    }
    const apiClient = createApiClient();
    const response = await apiClient.delete(`/user/${encodeURIComponent(userId)}`);
    return response.data;
  },

  // Resend invitation without creating user
  resendInvitation: async (email: string) => {
    if (!isValidEmail(email)) {
      throw new Error('Invalid email format');
    }
    const apiClient = createApiClient();
    const response = await apiClient.post('/user', {
      email,
      resend: true,
    });
    return response.data;
  },
}; 